/**
 * Custom Accordion Component - Japan Scissors Theme
 * Updated with unique class names to avoid conflicts
 */

class JapanScissorsAccordion {
    constructor() {
        this.accordions = [];
        this.init();
    }

    init() {
        // Initialize all accordions on the page
        this.initializeAccordions();

        // Reinitialize when new content is loaded (for AJAX)
        document.addEventListener('shopify:section:load', () => {
            this.initializeAccordions();
        });

        // Also initialize on DOMContentLoaded in case script loads early
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.initializeAccordions();
            });
        }
    }

    initializeAccordions() {
        // Find all accordion containers with our unique class
        const containers = document.querySelectorAll('.js-scissors-accordion');

        containers.forEach(container => {
            if (!container.dataset.accordionInitialized) {
                this.setupAccordion(container);
                container.dataset.accordionInitialized = 'true';
            }
        });
    }

    setupAccordion(container) {
        const items = container.querySelectorAll('.js-accordion-item');

        items.forEach(item => {
            const trigger = item.querySelector('.js-accordion-trigger');
            if (!trigger) return;

            // Make accessible
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('tabindex', '0');
            trigger.setAttribute('aria-expanded', 'false');

            const panel = item.querySelector('.js-accordion-panel');
            if (panel) {
                // Generate unique ID for ARIA
                const panelId = 'js-accordion-panel-' + Math.random().toString(36).substr(2, 9);
                panel.id = panelId;
                trigger.setAttribute('aria-controls', panelId);
            }

            // Remove any existing listeners to prevent duplicates
            trigger.removeEventListener('click', this.handleClick);
            trigger.removeEventListener('keydown', this.handleKeydown);

            // Click handler
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleAccordion(item, container);
            });

            // Keyboard support
            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.toggleAccordion(item, container);
                }
            });
        });

        // Open first accordion by default if specified
        if (container.dataset.openFirst === 'true') {
            const firstItem = items[0];
            if (firstItem) {
                this.openAccordion(firstItem);
            }
        }
    }

    toggleAccordion(item, container) {
        const isActive = item.classList.contains('js-active');

        // Check if only one should be open at a time
        if (container.dataset.oneAtTime === 'true') {
            const allItems = container.querySelectorAll('.js-accordion-item');
            allItems.forEach(i => {
                if (i !== item) {
                    this.closeAccordion(i);
                }
            });
        }

        // Toggle current item
        if (isActive) {
            this.closeAccordion(item);
        } else {
            this.openAccordion(item);

            // Smooth scroll on mobile
            if (window.innerWidth <= 768) {
                setTimeout(() => {
                    const trigger = item.querySelector('.js-accordion-trigger');
                    const triggerRect = trigger.getBoundingClientRect();
                    const offset = 100; // Account for sticky header

                    if (triggerRect.top < offset) {
                        window.scrollTo({
                            top: triggerRect.top + window.pageYOffset - offset,
                            behavior: 'smooth'
                        });
                    }
                }, 300);
            }
        }
    }

    openAccordion(item) {
        item.classList.add('js-active');
        const trigger = item.querySelector('.js-accordion-trigger');
        const panel = item.querySelector('.js-accordion-panel');

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'true');
        }

        if (panel) {
            const body = panel.querySelector('.js-accordion-body');
            if (body) {
                // Set max-height dynamically for smooth animation
                panel.style.maxHeight = body.scrollHeight + 'px';
            }
        }
    }

    closeAccordion(item) {
        item.classList.remove('js-active');
        const trigger = item.querySelector('.js-accordion-trigger');
        const panel = item.querySelector('.js-accordion-panel');

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
        }

        if (panel) {
            panel.style.maxHeight = '0';
        }
    }

    // Public method to open/close specific accordion
    toggleById(itemId) {
        const item = document.getElementById(itemId);
        if (item && item.classList.contains('js-accordion-item')) {
            const container = item.closest('.js-scissors-accordion');
            if (container) {
                this.toggleAccordion(item, container);
            }
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.japanScissorsAccordion = new JapanScissorsAccordion();
    });
} else {
    window.japanScissorsAccordion = new JapanScissorsAccordion();
}

// Log for debugging